import { createAsyncThunk } from '@reduxjs/toolkit';
import api from 'api';
import { IDocument } from 'interfaces';
import dayjs from 'dayjs';
import { CancelTokenSource } from 'axios';
import { setDataRoom, setNotificationsStatus } from 'store/slices/dataRoomSlice';
import { NavigateFunction } from 'react-router';
import { RootState } from 'store';
import { clearReduxFunction } from './userDataCreator';
import { setRooms, setUserAndRoomStatus } from 'store/slices/userDataSlice';
import { setFoldersForPermissions, setUserPermissions } from 'store/slices/dataPermissionsSlice';
import { setIsRSidebarCollapsed, setMoreInfoHidden } from 'store/slices/windowStateSlice';
import { setCurrentFolder, setDocuments, setEmptyText, setSelectedDocument, setSelectedFolder, setViewType } from 'store/slices/dataDocumentsSlice';
import { TFunction } from 'i18next';
import { clearAllChatData, setAllTagsDocuments } from 'store/slices/chatsSlice';

export const fetchRoom = createAsyncThunk('dataRoom/fetchAll', async (props: { id: string, navigate?: NavigateFunction, message?: any}, thunkAPI) => {
  const rootState = thunkAPI.getState() as RootState;
  const { roomChatLoading, chatsAbortController } = rootState.chat;
  roomChatLoading && chatsAbortController?.abort();

  try {
    const rooms = await api.getRoom(props.id);    
    return {
      ...rooms.data,
      description: rooms.data.description ? rooms.data.description : '',
    };
  } catch (e) {
    props.message && props.message();
    props.navigate && props.navigate('/rooms');
    return thunkAPI.rejectWithValue('Download room failed');
  }
});

export const fetchConfidentialDocuments = createAsyncThunk(
  'confidentialDocuments/fetchAll',
  async (props: { room_id: string }, thunkAPI) => {
    try {
      const documents = await api.getConfidentialDocuments(props.room_id);
      const offset = new Date().getTimezoneOffset() * 60 * 1000;

      const preparedDocuments = documents.data.map((document: IDocument) => {
        const inSeconds = dayjs(document.upload_at).valueOf();
        const rightTime = document.upload_at ? dayjs(inSeconds - offset).format('DD.MM.YYYY, HH:mm:ss') : '-';
        return {
          ...document,
          upload_at: rightTime,
          key: document.token,
        };
      });
      return preparedDocuments;
    } catch (e) {
      return thunkAPI.rejectWithValue('Download failed');
    }
  }
);

export const fetchRoomNotifications = createAsyncThunk(
  'notifications/fetchAll',
  async (payload: { locale?: string, source?: CancelTokenSource }, thunkAPI) => {
    const { notificationPagination } = (thunkAPI.getState() as RootState).dataRoom;
    const { dispatch } = thunkAPI;
    const locale = payload.locale ? payload.locale : localStorage.getItem('language') || 'en';

    try {
      const notifications = await api.getNotificationsV2({
        locale,
        page: notificationPagination.current,
        per_page: notificationPagination.per_page,
      }, payload?.source?.token);

      setTimeout(() => {
        dispatch(setNotificationsStatus('fulfilled'));
      }, 10);
      return notifications.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err);
    }
  }
);

export const fetchNotificationUnviewed = createAsyncThunk(
  'notifications/fetchUnviewed',
  async (payload: {}, thunkAPI) => {
    const { userData } = (thunkAPI.getState() as RootState).userData;

    try {
      if (userData?.id) {
        const notifications = await api.getNotificationsUnviewedCount(userData?.id);
        return notifications.data.count_of_new;
      } else {
        return 0
      }
    } catch (err) {
      return thunkAPI.rejectWithValue(err)
    }
  }
);

export const kickUserCheck = createAsyncThunk(
  'kickUserCheck/ckeck',
  async (payload: {idRoom: string, navigate: NavigateFunction}, thunkAPI) => {
    const { dispatch } = thunkAPI;
    const state = thunkAPI.getState() as RootState;
    const { dataRoom } = state.dataRoom;
    const { available_rooms } = state.userData;


    try {
      if (payload.idRoom === dataRoom?.id) {
        dispatch(clearReduxFunction({}));
        dispatch(setAllTagsDocuments([]));
        dispatch(setUserAndRoomStatus(''))
        dispatch(setFoldersForPermissions([]));
        // dispatch(setSelectedKeys([`rooms`]))
        payload.navigate('/rooms');
      };
      const filteredRooms = available_rooms.filter((available_room) => available_room.id !== payload.idRoom);
      dispatch(setRooms(filteredRooms));
    } catch (err) {
      return thunkAPI.rejectWithValue(err)
    }
  }
);

export const blockRoom = createAsyncThunk(
  'blockRoom/ckeck',
  async (props: {data: any, navigate: NavigateFunction, t: TFunction }, thunkAPI) => {
    const rootState = thunkAPI.getState() as RootState;
    const { dataRoom } = rootState.dataRoom;
    const { available_rooms } = rootState.userData;
    const { messageApi } = rootState.windowState;

    const { dispatch } = thunkAPI;

    const correctedRooms = available_rooms.map((available_room) => available_room.id === props.data.room_id
      ? { ...available_room, is_blocked: props.data.is_blocked }
      : available_room
    );
    dispatch(setRooms(correctedRooms));

    if (props.data.room_id === dataRoom?.id) {

      if (props.data.is_blocked) {
        dispatch(setEmptyText('blocked'));
        dispatch(setDocuments({ documents: [], folders: [] }));
        localStorage.setItem('previousFolderPath', JSON.stringify([]));
        dispatch(setViewType('Table'));
        dispatch(setSelectedDocument(null));
        dispatch(setSelectedFolder(null));
        dispatch(setMoreInfoHidden(true));
        dispatch(setCurrentFolder([]));
        dispatch(setIsRSidebarCollapsed({collapsed: true, entity: '', width: 0}));
        dispatch(setDataRoom({...dataRoom!, is_blocked: props.data.is_blocked}));
        dispatch(clearAllChatData());
        props.navigate(`/room/${dataRoom?.id}/documents`);
        
        messageApi?.error(props.t('Rooms.errors.blocked'), 10);
      } else {
        dispatch(setEmptyText('fallback'));
        dispatch(setDataRoom({...dataRoom!, is_blocked: props.data.is_blocked}));        
        dispatch(setUserPermissions(null));
        dispatch(setUserAndRoomStatus('fulfilledUserData'));

        messageApi?.success(props.t('Rooms.success.unblocked'), 10);
      }
    };
  }
);


